/** HTTP status the measurement API answers with when a client exceeds its request budget. */
export const TOO_MANY_REQUESTS_STATUS = 429;

/** Bounds (ms) of the random delay before a rate-limited request is retried. */
export const RATE_LIMIT_RETRY_MIN_DELAY_MS = 1_000;
export const RATE_LIMIT_RETRY_MAX_DELAY_MS = 10_000;

/** TanStack Query's own default (`retry: 3`), kept so non-429 failures behave exactly as before. */
const DEFAULT_RETRY_COUNT = 3;
const DEFAULT_RETRY_BASE_DELAY_MS = 1_000;
const DEFAULT_RETRY_MAX_DELAY_MS = 30_000;

/**
 * Thrown when the API answers HTTP 429. Rate limiting is transient, so queries treat this
 * differently from every other failure: they keep retrying instead of surfacing an error.
 */
export class TooManyRequestsError extends Error {
  readonly status = TOO_MANY_REQUESTS_STATUS;

  constructor(message = 'Too many requests: the server is rate limiting this client.') {
    super(message);
    this.name = 'TooManyRequestsError';
  }
}

/**
 * Duck-typed on `status` as well as the class so an error that crossed a `Promise.allSettled`
 * boundary (or was built by a different module instance) is still recognised.
 */
export const isTooManyRequestsError = (error: unknown): boolean => {
  if (error instanceof TooManyRequestsError) return true;
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: unknown }).status === TOO_MANY_REQUESTS_STATUS
  );
};

/**
 * Rethrows the first 429 among settled results. Fetchers that fan out with `Promise.allSettled`
 * drop rejected requests, which would otherwise hide rate limiting behind a partially filled
 * (and silently wrong) success.
 */
export const throwIfTooManyRequests = (results: PromiseSettledResult<unknown>[]): void => {
  const rateLimited = results.find(
    (result): result is PromiseRejectedResult =>
      result.status === 'rejected' && isTooManyRequestsError(result.reason)
  );
  if (rateLimited) throw rateLimited.reason;
};

/**
 * Retries HTTP 429 indefinitely; every other error keeps TanStack Query's default of 3 attempts.
 */
export const retryUnlessRateLimitClears = (failureCount: number, error: unknown): boolean =>
  isTooManyRequestsError(error) || failureCount < DEFAULT_RETRY_COUNT;

/**
 * Random 1–10s for 429 so a whole page of widgets does not hammer the API back in lockstep;
 * exponential backoff (TanStack Query's default curve) for everything else.
 */
export const getRetryDelayMs = (failureCount: number, error: unknown): number => {
  if (isTooManyRequestsError(error)) {
    return (
      RATE_LIMIT_RETRY_MIN_DELAY_MS +
      Math.random() * (RATE_LIMIT_RETRY_MAX_DELAY_MS - RATE_LIMIT_RETRY_MIN_DELAY_MS)
    );
  }
  return Math.min(DEFAULT_RETRY_BASE_DELAY_MS * 2 ** failureCount, DEFAULT_RETRY_MAX_DELAY_MS);
};

/**
 * Query options that keep a request alive across rate limiting. Spread into `useQuery`.
 */
export const rateLimitRetryOptions = {
  retry: retryUnlessRateLimitClears,
  retryDelay: getRetryDelayMs,
} as const;
